/**
 * The invitation form's decisions, pure.
 *
 * 🔴 IT LIVES HERE AND NOT IN THE `.tsx` FOR THE REASON EVERY OTHER MODEL FILE
 * IN THIS REPO DOES: there is no DOM renderer (OD-60), so a rule written inside
 * a component is a rule no test can reach — and this one guards the moment a
 * staff credential is set.
 */

import { DOOR_ROUTE, doorFor, type RouteId } from '../../../app/router/routes'
import { formatIst } from '../../../shared/lib/ist'
import type { InvitePreview } from '../../../shared/api/auth'
import type { Role } from '../../../shared/types/prisma-enums'

/**
 * Where "Sign in" goes once the password is set: the door for the role that
 * was just claimed.
 *
 * 🔴 IT WAS ALWAYS THE TEACHER DOOR, and a new school admin's correct password
 * was refused there — and counted toward their lock-out. `doorFor` is the one
 * place that decides which tier a role signs in at, so this reads it rather
 * than keeping a second list of roles.
 *
 * ⚠️ AN UNKNOWN ROLE (an older server that does not say) KEEPS THE OLD ANSWER,
 * the teacher door, where the screen still offers the school admin link.
 */
export function signInRouteAfterClaim(role: Role | null | undefined): RouteId {
  const door = role ? doorFor(role) : null
  return door ? DOOR_ROUTE[door] : 'staff-login'
}

/**
 * 🔴 A DELIBERATE SECOND COPY OF THE SERVER'S PASSWORD RULE (P2-02, Figma P4).
 *
 * The server refuses a password that breaks it with a 400, and that refusal is
 * the control. These numbers exist so the person is told while the box is still
 * under their cursor, rather than after a round trip that reads as the form
 * being broken. ⚠️ If the server's rule moves, this must move with it — the
 * failure mode is a form that accepts what the API then rejects.
 */
export const MIN_PASSWORD_LENGTH = 10
export const MAX_PASSWORD_LENGTH = 200

export type PasswordProblem = 'TOO_SHORT' | 'TOO_LONG' | 'NO_NUMBER' | 'NO_CAPITAL' | 'MISMATCH'

/**
 * The live checklist under the new-password field (Figma P4).
 *
 * ✅ ALL THREE ARE RULES NOW. They were advice ("recommended") because the
 * server required only the length; the set-password surfaces (claim, reset,
 * forced change) now require a digit and a capital too, and a checklist that
 * called a requirement "recommended" would be a form lying about why its button
 * will not work.
 *
 * ⚠️ `capital` IS A–Z, NOT ANY UPPERCASE LETTER. The server's check is the ASCII
 * range; `\p{Lu}` here would tick the box for "É" and the API would then refuse
 * the password the checklist had approved.
 */
export interface PasswordChecks {
  length: boolean
  number: boolean
  capital: boolean
}

export function passwordChecks(password: string): PasswordChecks {
  return {
    length: password.length >= MIN_PASSWORD_LENGTH,
    number: /\d/.test(password),
    capital: /[A-Z]/.test(password),
  }
}

/**
 * What is wrong with the pair, or `null` if nothing is.
 *
 * ⚠️ THE RULE IS CHECKED BEFORE THE MATCH, so somebody typing a short password
 * into both boxes is told the useful thing rather than being told they match.
 *
 * 🔴 THE PASSWORD IS NOT TRIMMED, AND THAT IS NOT AN OVERSIGHT. A leading or
 * trailing space is a legitimate character in a password — trimming here would
 * silently store something different from what the person typed, and they would
 * then fail to sign in with the exact string they chose.
 */
export function passwordProblem(password: string, confirm: string): PasswordProblem | null {
  const checks = passwordChecks(password)
  if (!checks.length) return 'TOO_SHORT'
  if (password.length > MAX_PASSWORD_LENGTH) return 'TOO_LONG'
  if (!checks.number) return 'NO_NUMBER'
  if (!checks.capital) return 'NO_CAPITAL'
  if (password !== confirm) return 'MISMATCH'
  return null
}

/** The three checklist lines, worded once for every set-password screen. */
export function passwordRuleItems(password: string): { label: string; met: boolean }[] {
  const checks = passwordChecks(password)
  return [
    { label: `At least ${String(MIN_PASSWORD_LENGTH)} characters`, met: checks.length },
    { label: 'One number', met: checks.number },
    { label: 'One capital letter', met: checks.capital },
  ]
}

/* ────────────────────────────────────── P2-02 · the personalised page ── */

/**
 * "Welcome to Kalqy, Greenwood" — the school's everyday name, from its full one.
 *
 * ⚠️ ONLY A KNOWN TAIL IS DROPPED ("International School", "Public School",
 * "School", …). Taking the first word instead would greet St. Mary's as "St.",
 * and a name with no such tail is already the everyday name.
 */
const SCHOOL_TAIL =
  /\s+(?:(?:international|public|high|higher secondary|senior secondary|primary|montessori|play|pre-?primary|english medium|model)\s+)?(?:school|schools)$/i

export function shortSchoolName(name: string): string {
  const trimmed = name.trim()
  const short = trimmed.replace(SCHOOL_TAIL, '').trim()
  return short.length > 0 ? short : trimmed
}

/** What the role was invited AS, in the sentence under the title. */
const ROLE_PHRASE: Record<NonNullable<InvitePreview['role']>, string> = {
  ADMIN: 'the school admin',
  PRINCIPAL: 'the principal',
  TEACHER: 'a teacher',
  COORDINATOR: 'a coordinator',
  SUPER_ADMIN: 'a member of the Kalqy team',
}

export interface InviteGreeting {
  /** Brand-panel headline. */
  headline: string
  /** The form's subtitle — who set this up and as what. */
  invitedLine: string
  /** The first brand-panel point. */
  approvedTitle: string
  approvedBody: string
  /** "This invite link expires on 15 Oct 2026." in IST, or the rule when unknown. */
  expiryLine: string
  /** Figma's "Approved by Kalqy → Set password → Pay" is the school admin's journey only. */
  journey: 'school-admin' | 'staff'
}

/**
 * Everything the set-password page says that depends on WHO the link is for.
 *
 * 🔴 A NULL OR INVALID PREVIEW GIVES THE GENERIC PAGE, NEVER A GUESS. The preview
 * may fail (an older server, a dropped connection) or come back `valid: false`;
 * in both cases the copy has to be true for any invitee, exactly as it was before
 * the preview existed. Inventing a school name would be worse than none.
 */
export function inviteGreeting(preview: InvitePreview | null): InviteGreeting {
  const live = preview !== null && preview.valid ? preview : null
  const school = live?.schoolName ?? null
  const role = live?.role ?? null
  const journey: InviteGreeting['journey'] = role === 'ADMIN' ? 'school-admin' : 'staff'

  const headline = school ? `Welcome to Kalqy, ${shortSchoolName(school)}` : 'Welcome to Kalqy'

  let invitedLine = 'You were invited to Kalqy. Choose a password to finish setting up your account.'
  if (role === 'ADMIN' && school) {
    invitedLine = `Kalqy set up ${school} and invited you as the school admin.`
  } else if (role === 'SUPER_ADMIN') {
    invitedLine = 'A Kalqy operator invited you to the Kalqy team. You will set up two-step sign-in next.'
  } else if (role && school) {
    invitedLine = `${school} invited you to Kalqy as ${ROLE_PHRASE[role]}.`
  }

  const facts: string[] = []
  if (live?.children !== null && live?.children !== undefined) facts.push(`${String(live.children)} children`)
  if (live?.teachers !== null && live?.teachers !== undefined) facts.push(`${String(live.teachers)} teachers`)
  if (live?.planName) facts.push(live.planName)

  const approvedTitle = journey === 'school-admin' ? 'Your school is approved' : 'Your account is ready'
  const approvedBody =
    facts.length > 0
      ? facts.join(' · ')
      : journey === 'school-admin'
        ? 'Kalqy has set up your school and its plan.'
        : 'Your school or Kalqy set it up and sent you this invitation.'

  const expires = live?.expiresAt ? new Date(live.expiresAt) : null
  const expiryLine =
    expires !== null && !Number.isNaN(expires.getTime())
      ? `This invite link expires on ${formatIst(expires)}.`
      : 'Invite links work once and expire 72 hours after they are sent.'

  return { headline, invitedLine, approvedTitle, approvedBody, expiryLine, journey }
}
